import { action, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';

import { equalsDeep } from '../../../shared/equalsDeep';
import type { TUserData } from '../../../shared/types';
import type { TRiskLevel, TUserInfo } from '../../types';
import type { TManualAnomalyRowData } from '../types';

export type TData = { [key: string]: unknown | null } & {
  [Key in keyof Partial<TManualAnomalyRowData>]: TManualAnomalyRowData[Key] | null;
} & { riskLevel: TRiskLevel };

export class ManualAnomaly {
  readonly entityId = v4();
  readonly id: string | null;
  readonly initialData: TData;
  readonly userData: TUserData;
  readonly createdAt: string;
  readonly createdBy: TUserInfo | null;

  dirty = false;

  data: TData;

  constructor(initialData: TData, userData: TUserData) {
    this.data = initialData;
    this.initialData = initialData;
    this.id = initialData.id ?? null;
    this.userData = userData;
    this.createdAt = initialData.createdAt ?? new Date().toISOString();
    this.createdBy =
      initialData.createdBy ??
      (this.id == null
        ? {
            firstName: userData.given_name,
            lastName: userData.family_name,
            username: userData.preferred_username,
            sub: userData.sub,
          }
        : null);

    makeObservable(this, {
      data: observable,
      updateData: action,
    });
  }

  updateData(updateData: Partial<TData>): void {
    this.data = {
      ...this.data,
      ...updateData,
    };
    this.dirty = true;
  }

  deepEquals(other: TData): boolean {
    return equalsDeep(this.data, other.data);
  }

  get isChanged(): boolean {
    return this.dirty && !equalsDeep(this.data, this.initialData);
  }
}
